import { Link, NavLink, Outlet } from 'react-router-dom';
import { BookOpen, Heart, History, Settings, Library, Menu, Sparkles } from 'lucide-react';

import { SourcePicker } from './SourcePicker';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { AccountButton } from '@/features/account';

const navLinks: Array<{ to: string; label: string; icon: typeof BookOpen }> = [
  { to: '/', label: 'Discover', icon: Sparkles },
  { to: '/library/favorites', label: 'Favorites', icon: Heart },
  { to: '/library/history', label: 'History', icon: History },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const mobileNavLinks: Array<{ to: string; label: string; icon: typeof BookOpen; end?: boolean }> = [
  { to: '/', label: 'Discover', icon: Sparkles, end: true },
  { to: '/library', label: 'Saved', icon: Library },
  { to: '/library/history', label: 'History', icon: History },
  { to: '/more', label: 'More', icon: Menu },
];

export function Layout() {
  return (
    <div className="flex min-h-full flex-col">
      <div
        aria-hidden="true"
        data-testid="mobile-safe-area-top"
        className="fixed inset-x-0 top-0 z-40 h-[var(--app-safe-area-top)] bg-background/95 backdrop-blur md:hidden"
      />
      <header className="sticky top-0 z-40 hidden w-full border-b border-border/60 bg-background/80 pt-[var(--app-safe-area-top)] backdrop-blur md:block">
        <div className="container flex h-14 items-center gap-4">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <Library className="h-5 w-5" />
            <span>Bunko</span>
          </Link>

          <div className="hidden md:block">
            <SourcePicker />
          </div>

          <div className="flex-1" />

          <nav className="flex items-center gap-1">
            <AccountButton />
            {navLinks.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to}>
                {({ isActive }) => (
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    size="sm"
                    className={cn('gap-1.5')}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{label}</span>
                  </Button>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="container flex-1 pb-[calc(5rem_+_var(--app-safe-area-bottom))] pt-[calc(1rem_+_var(--app-safe-area-top))] md:py-6">
        <Outlet />
      </main>

      <MobileNav />

      <footer className="hidden border-t border-border/60 pb-[calc(1rem_+_var(--app-safe-area-bottom))] pt-4 md:block">
        <div className="container text-xs text-muted-foreground">
          Bunko — personal use. Respect each source site&apos;s terms of service.
        </div>
      </footer>
    </div>
  );
}

function MobileNav() {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-border/60 bg-background/95 px-2 pb-[calc(0.5rem_+_var(--app-safe-area-bottom))] pt-2 backdrop-blur md:hidden">
      <div className="grid grid-cols-4 gap-1">
        {mobileNavLinks.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end}>
            {({ isActive }) => (
              <span
                className={cn(
                  'flex min-h-12 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-medium transition-colors',
                  isActive
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
